import React, { useState, useEffect } from 'react';
import {
  IonContent,
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonMenuButton,
  IonButton,
  IonIcon,
  IonGrid,
  IonRow,
  IonCol,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonItem,
  IonLabel,
  IonBadge,
  IonSearchbar,
  IonFab,
  IonFabButton,
  IonModal,
  IonInput,
  IonSelect,
  IonSelectOption,
  IonToast,
  IonSpinner,
  IonRefresher,
  IonRefresherContent,
  RefresherEventDetail,
  IonList,
  IonChip,
  IonText,
  IonDatetime,
  IonActionSheet,
  IonAlert,
} from '@ionic/react';
import {
  addOutline,
  cashOutline,
  personOutline,
  mailOutline,
  callOutline,
  cardOutline,
  walletOutline,
  checkmarkCircleOutline,
  closeCircleOutline,
  ellipsisVertical,
  arrowUndoOutline,
  banOutline,
  ticketOutline,
  downloadOutline,
  cloudUploadOutline,
} from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { useEvent } from '../contexts/EventContext';
import { saleService, ticketService } from '../services/api';
import { Sale, Ticket, Event } from '../types';
import { format } from 'date-fns';
import EventSelector from '../components/EventSelector';
import EnhancedEventSelector from '../components/EnhancedEventSelector';
import EventSelectOrCreate from '../components/EventSelectOrCreate';
import CreateEventForm from '../components/CreateEventForm';
import SourceDetailsModal from '../components/SourceDetailsModal';
import { useLocation } from 'react-router-dom';
import CenteredLoader from '../components/CenteredLoader';
import { eventBus, EVENTS } from '../utils/eventBus';
import { generateSalesTemplate, parseSalesExcel } from '../utils/excelUtils';

const Sales: React.FC = () => {
  const { t } = useTranslation();
  const { currentEvent, setCurrentEvent, loading: eventsLoading } = useEvent();
  const location = useLocation<{ openNewSale?: boolean }>();
  const [sales, setSales] = useState<Sale[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [searchText, setSearchText] = useState('');
  const [availableTickets, setAvailableTickets] = useState<Ticket[]>([]);
  const [selectedSale, setSelectedSale] = useState<Sale | null>(null);
  const [showActionSheet, setShowActionSheet] = useState(false);
  const [showCancelAlert, setShowCancelAlert] = useState(false);
  const [showRefundAlert, setShowRefundAlert] = useState(false);
  const [showCreateEventModal, setShowCreateEventModal] = useState(false);
  const [selectedEventForSale, setSelectedEventForSale] =
    useState<Event | null>(currentEvent);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [showSourceModal, setShowSourceModal] = useState(false);
  const [selectedSaleForSource, setSelectedSaleForSource] = useState<Sale | null>(null);

  const [formData, setFormData] = useState({
    ticketIds: [] as string[],
    buyerName: '',
    buyerEmail: '',
    buyerPhone: '',
    salePrice: 0,
    paymentMethod: 'cash' as 'cash' | 'card' | 'bankTransfer' | 'other',
    saleDate: new Date(),
  });

  useEffect(() => {
    if (currentEvent) {
      loadSales();
      loadAvailableTickets();
    } else {
      // If no event is selected, stop loading
      setLoading(false);
      setSales([]);
      setAvailableTickets([]);
    }
  }, [currentEvent]);

  useEffect(() => {
    // Subscribe to event bus for refreshing sales
    const unsubscribeSales = eventBus.on(EVENTS.REFRESH_SALES, () => {
      console.log('Refreshing sales due to email fetch');
      if (currentEvent) {
        loadSales();
      }
    });

    const unsubscribeTickets = eventBus.on(EVENTS.REFRESH_TICKETS, () => {
      console.log('Refreshing tickets due to email fetch');
      if (currentEvent) {
        loadAvailableTickets();
      }
    });

    const unsubscribeSalesCreated = eventBus.on(
      EVENTS.SALES_CREATED_FROM_EMAIL,
      (data: { count: number }) => {
        setToastMessage(
          `${data.count} new sale${
            data.count > 1 ? 's' : ''
          } created from emails!`
        );
        setShowToast(true);
      }
    );

    // Cleanup subscriptions on unmount
    return () => {
      unsubscribeSales();
      unsubscribeTickets();
      unsubscribeSalesCreated();
    };
  }, [currentEvent]);

  useEffect(() => {
    // Check if we should open the new sale modal from navigation state
    if (location.state?.openNewSale) {
      setShowAddModal(true);
      // Clear the state to prevent reopening on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const loadSales = async () => {
    try {
      setError(null);
      setLoading(true);
      // Pass eventId only if an event is selected, otherwise get all sales
      const params = currentEvent ? { eventId: currentEvent._id } : {};
      const data = await saleService.getAll(params);
      console.log('Sales data received:', data);
      setSales(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err.message || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableTickets = async () => {
    if (!currentEvent) return;

    try {
      const tickets = await ticketService.getAll({
        eventId: currentEvent._id,
        status: 'available',
      });
      setAvailableTickets(tickets || []);
    } catch (err) {
      console.error('Failed to load available tickets', err);
    }
  };

  const loadAvailableTicketsForEvent = async (eventId: string) => {
    try {
      const tickets = await ticketService.getAll({
        eventId: eventId,
        status: 'available',
      });
      setAvailableTickets(tickets || []);
      // Reset selected tickets when event changes
      setFormData((prev) => ({ ...prev, ticketIds: [] }));
    } catch (err) {
      console.error('Failed to load available tickets', err);
      setAvailableTickets([]);
    }
  };

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    await Promise.all([loadSales(), loadAvailableTickets()]);
    event.detail.complete();
  };

  const calculateTotalAmount = () => {
    const selectedTickets = availableTickets.filter((t) =>
      formData.ticketIds.includes(t._id || t.id || '')
    );
    return selectedTickets.reduce((sum, ticket) => sum + formData.salePrice, 0);
  };

  const calculateNetProfit = () => {
    const selectedTickets = availableTickets.filter((t) =>
      formData.ticketIds.includes(t._id || t.id || '')
    );
    const totalCost = selectedTickets.reduce(
      (sum, ticket) => sum + ticket.purchasePrice,
      0
    );
    const totalRevenue = formData.salePrice * selectedTickets.length;
    return totalRevenue - totalCost;
  };

  const handleRecordSale = async () => {
    // Validate required fields
    if (formData.ticketIds.length === 0) {
      setToastMessage(t('sales.selectTicketsRequired'));
      setShowToast(true);
      return;
    }

    if (!formData.buyerName || !formData.buyerEmail || !formData.buyerPhone) {
      setToastMessage(t('sales.buyerInfoRequired'));
      setShowToast(true);
      return;
    }

    if (!formData.salePrice || formData.salePrice <= 0) {
      setToastMessage(t('sales.salePriceRequired'));
      setShowToast(true);
      return;
    }

    try {
      // Try different date formats to see which one works
      const currentDate = new Date();
      const dateFormats = {
        full: currentDate.toISOString(), // "2023-08-29T12:01:19.649Z"
        noMillis: currentDate.toISOString().split('.')[0] + 'Z', // "2023-08-29T12:01:19Z"
        dateOnly: currentDate.toISOString().split('T')[0], // "2023-08-29"
      };

      console.log('Trying date formats:', dateFormats);

      // Only send the fields expected by the backend
      const saleData = {
        ticketIds: formData.ticketIds, // Already an array of strings
        buyerName: formData.buyerName.trim(),
        buyerEmail: formData.buyerEmail.trim(),
        buyerPhone: formData.buyerPhone.trim(),
        salePrice: Number(formData.salePrice) * formData.ticketIds.length, // Ensure it's a number
        paymentMethod: formData.paymentMethod,
        saleDate: new Date().toISOString(), // Direct ISO string
      };

      console.log('Sending sale data:', JSON.stringify(saleData, null, 2));
      console.log('Date string format:', saleData.saleDate);
      console.log('Type of saleDate:', typeof saleData.saleDate);

      await saleService.create(saleData);
      setToastMessage(t('sales.saleSuccess'));
      setShowToast(true);
      setShowAddModal(false);
      resetForm();
      await Promise.all([loadSales(), loadAvailableTickets()]);

      // Notify other components to refresh
      eventBus.emit(EVENTS.REFRESH_SALES);
      eventBus.emit(EVENTS.REFRESH_TICKETS);
    } catch (err: any) {
      setToastMessage(err.message || t('errors.generic'));
      setShowToast(true);
    }
  };

  const handleCancelSale = async () => {
    if (!selectedSale) return;

    try {
      await saleService.cancel(selectedSale.id!);
      setToastMessage(t('sales.cancelSuccess'));
      setShowToast(true);
      setShowCancelAlert(false);
      await Promise.all([loadSales(), loadAvailableTickets()]);

      // Notify other components to refresh
      eventBus.emit(EVENTS.REFRESH_TICKETS);
      eventBus.emit(EVENTS.REFRESH_SALES);
    } catch (err: any) {
      setToastMessage(err.message || t('errors.generic'));
      setShowToast(true);
    }
  };

  const resetForm = () => {
    setFormData({
      ticketIds: [],
      buyerName: '',
      buyerEmail: '',
      buyerPhone: '',
      salePrice: 0,
      paymentMethod: 'cash',
      saleDate: new Date(),
    });
  };

  const getPaymentIcon = (method: string) => {
    switch (method) {
      case 'cash':
        return cashOutline;
      case 'card':
        return cardOutline;
      case 'bankTransfer':
        return walletOutline;
      default:
        return cashOutline;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'danger';
      case 'refunded':
        return 'warning';
      default:
        return 'medium';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return checkmarkCircleOutline;
      case 'cancelled':
        return closeCircleOutline;
      case 'refunded':
        return arrowUndoOutline;
      default:
        return checkmarkCircleOutline;
    }
  };

  const handleDownloadTemplate = () => {
    try {
      generateSalesTemplate();
      setToastMessage('Sales template downloaded successfully');
      setShowToast(true);
    } catch (error) {
      console.error('Error downloading template:', error);
      setToastMessage('Failed to download template');
      setShowToast(true);
    }
  };

  const handleShowSaleSource = async (sale: Sale) => {
    try {
      // Get the full ticket details for this sale to show the source
      if (sale.ticketIds && sale.ticketIds.length > 0) {
        // Fetch the first ticket's details to get source information
        const ticketResponse = await ticketService.getById(sale.ticketIds[0]);
        if (ticketResponse) {
          // Add the ticket info to the sale object
          const saleWithTicket = {
            ...sale,
            firstTicket: ticketResponse
          };
          setSelectedSaleForSource(saleWithTicket);
          setShowSourceModal(true);
        }
      } else {
        // If no tickets, show the sale details only
        setSelectedSaleForSource(sale);
        setShowSourceModal(true);
      }
    } catch (error) {
      console.error('Error fetching ticket details:', error);
      // Show sale details without source info
      setSelectedSaleForSource(sale);
      setShowSourceModal(true);
    }
  };

  const getSourceTypeFromSale = (sale: Sale): 'supplier' | 'buyer' | 'site' | 'private' => {
    // Check if sale has ticket information
    const firstTicket = (sale as any).firstTicket;
    if (firstTicket) {
      if (firstTicket.sourceType === 'supplier') return 'supplier';
      if (firstTicket.sourceType === 'site') return 'site';
      if (firstTicket.sourceType === 'private') return 'private';
    }
    
    // Fallback: if sale has supplier info, it's from supplier
    if (sale.supplierId || sale.saleType === 'supplier') return 'supplier';
    
    // Default to showing buyer details for sales
    return 'buyer';
  };

  const getSourceDataFromSale = (sale: Sale) => {
    const sourceType = getSourceTypeFromSale(sale);
    const firstTicket = (sale as any).firstTicket;
    
    if (sourceType === 'supplier') {
      // Return supplier information from ticket or sale
      if (firstTicket && typeof firstTicket.supplierId === 'object') {
        return firstTicket.supplierId; // Populated supplier object
      }
      if (typeof sale.supplierId === 'object') {
        return sale.supplierId; // Populated supplier object
      }
      // Create a basic supplier object with available info
      return {
        name: firstTicket?.supplier || sale.source || 'Unknown Supplier',
        id: firstTicket?.supplierId || sale.supplierId
      };
    }
    
    if (sourceType === 'site') {
      return firstTicket || sale;
    }
    
    if (sourceType === 'buyer') {
      return sale; // Return the sale object for buyer details
    }
    
    return null; // Private transaction
  };

  const handleImportExcel = async () => {
    if (!importFile) {
      setToastMessage('Please select a file to import');
      setShowToast(true);
      return;
    }

    if (!currentEvent) {
      setToastMessage('Please select an event first');
      setShowToast(true);
      return;
    }

    setImporting(true);
    try {
      // Parse the Excel file
      const parsedSales = await parseSalesExcel(importFile, [currentEvent]);

      // Import each sale
      let successCount = 0;
      let errorCount = 0;

      for (const saleData of parsedSales) {
        try {
          await saleService.create({
            ...saleData,
            eventId: currentEvent._id,
          } as any);
          successCount++;
        } catch (error) {
          console.error('Error importing sale:', error);
          errorCount++;
        }
      }

      setToastMessage(
        `Imported ${successCount} sales successfully${
          errorCount > 0 ? `, ${errorCount} failed` : ''
        }`
      );
      setShowToast(true);

      // Reload sales
      await loadSales();
      setShowImportModal(false);
      setImportFile(null);
    } catch (error: any) {
      console.error('Error importing Excel:', error);
      setToastMessage(error.message || 'Failed to import Excel file');
      setShowToast(true);
    } finally {
      setImporting(false);
    }
  };

  const filteredSales = Array.isArray(sales)
    ? sales.filter((sale) => {
        if (!searchText) return true;
        const searchLower = searchText.toLowerCase();
        return (
          sale.buyerName?.toLowerCase().includes(searchLower) ||
          sale.buyerEmail?.toLowerCase().includes(searchLower) ||
          sale.buyerPhone?.toLowerCase().includes(searchLower) ||
          (sale.ticketIds &&
            Array.isArray(sale.ticketIds) &&
            sale.ticketIds.some(
              (t: any) =>
                typeof t === 'object' &&
                t.eventName?.toLowerCase().includes(searchLower)
            )) ||
          sale.eventName?.toLowerCase().includes(searchLower)
        );
      })
    : [];

  console.log('Sales array:', sales);
  console.log('Filtered sales:', filteredSales);
  console.log('Search text:', searchText);

  if (loading || eventsLoading) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonMenuButton menu="main-menu" />
            </IonButtons>
            <IonTitle>{t('sales.title')}</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <CenteredLoader
            message={t('sales.loadingSales') || 'Loading sales...'}
          />
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton menu="main-menu" />
          </IonButtons>
          <IonTitle>{t('sales.title')}</IonTitle>
          <IonButtons slot="end">
            <IonButton
              onClick={handleDownloadTemplate}
              title="Download Excel Template"
            >
              <IonIcon icon={downloadOutline} />
            </IonButton>
            <IonButton
              onClick={() => setShowImportModal(true)}
              title="Import Excel"
            >
              <IonIcon icon={cloudUploadOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
        <IonToolbar>
          <IonSearchbar
            value={searchText}
            onIonChange={(e) => setSearchText(e.detail.value!)}
            placeholder={t('common.search')}
            className="px-2"
          />
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen className="bg-gray-50 dark:bg-gray-900">
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent></IonRefresherContent>
        </IonRefresher>

        <IonGrid className="p-4">
          <IonRow className="mb-4">
            <IonCol size="12">
              <EnhancedEventSelector
                currentEvent={currentEvent}
                onEventSelect={setCurrentEvent}
                onCreateEvent={() => setShowCreateEventModal(true)}
                showAllEvents={true}
              />
            </IonCol>
          </IonRow>

          <IonRow className="mt-4">
            <IonCol size="12">
              {filteredSales.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500 dark:text-gray-400">
                    {searchText
                      ? t('common.noResultsFound')
                      : t('sales.noSales')}
                  </p>
                </div>
              ) : (
                <IonCard className="shadow-lg">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('sales.saleDate')}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('sales.buyerName')}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('inventory.eventName')}
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('inventory.quantity')}
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('sales.totalAmount')}
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('sales.netProfit')}
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('sales.paymentMethod')}
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('inventory.status')}
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('common.actions')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                        {filteredSales.map((sale, index) => (
                          <tr
                            key={sale.id || `sale-${index}`}
                            className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer"
                            onClick={() => handleShowSaleSource(sale)}
                          >
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm text-gray-900 dark:text-gray-100">
                                {format(
                                  new Date(sale.saleDate),
                                  'MMM dd, yyyy'
                                )}
                                <span className="text-gray-500 dark:text-gray-400 ml-1 text-xs">
                                  {format(new Date(sale.saleDate), 'HH:mm')}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div>
                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {sale.buyerName}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  {sale.buyerEmail}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-sm text-gray-900 dark:text-gray-100 break-words">
                                {(sale.ticketIds as any)?.[0]?.eventName ||
                                  sale.eventName ||
                                  '-'}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-center">
                              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {sale.ticketIds?.length || sale.quantity || 0}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-right">
                              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                ${sale.totalAmount.toFixed(2)}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-right">
                              <div className="text-sm font-medium text-green-600 dark:text-green-400">
                                ${sale.netProfit.toFixed(2)}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-center">
                              <span className="text-sm text-gray-900 dark:text-gray-100">
                                {t(`sales.${sale.paymentMethod}`)}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-center">
                              <span
                                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  sale.status === 'confirmed'
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                    : sale.status === 'cancelled'
                                    ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                    : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                }`}
                              >
                                {sale.status}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-center">
                              <IonButton
                                fill="clear"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedSale(sale);
                                  setShowActionSheet(true);
                                }}
                                disabled={sale.status !== 'confirmed'}
                              >
                                <IonIcon icon={ellipsisVertical} />
                              </IonButton>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </IonCard>
              )}
            </IonCol>
          </IonRow>
        </IonGrid>

        <IonFab vertical="bottom" horizontal="end" slot="fixed">
          <IonFabButton
            onClick={() => {
              setSelectedEventForSale(currentEvent);
              if (currentEvent) {
                loadAvailableTicketsForEvent(currentEvent._id);
              }
              setShowAddModal(true);
            }}
          >
            <IonIcon icon={addOutline} />
          </IonFabButton>
        </IonFab>

        {/* Record Sale Modal */}
        <IonModal
          isOpen={showAddModal}
          onDidDismiss={() => {
            setShowAddModal(false);
            setSelectedEventForSale(currentEvent);
            setFormData({
              ticketIds: [],
              buyerName: '',
              buyerEmail: '',
              buyerPhone: '',
              salePrice: 0,
              paymentMethod: 'cash',
              saleDate: new Date(),
            });
          }}
          className="event-modal-fullscreen"
        >
          <IonHeader>
            <IonToolbar>
              <IonTitle>{t('sales.recordSale')}</IonTitle>
              <IonButtons slot="start">
                <IonButton onClick={() => setShowAddModal(false)}>
                  {t('common.cancel')}
                </IonButton>
              </IonButtons>
              <IonButtons slot="end">
                <IonButton
                  strong
                  onClick={handleRecordSale}
                  disabled={
                    !selectedEventForSale || formData.ticketIds.length === 0
                  }
                >
                  {t('common.save')}
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="p-4">
            {/* Event Selection Section */}
            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-3">
                {t('events.selectEvent')}
              </h3>
              <EventSelectOrCreate
                selectedEvent={selectedEventForSale}
                onEventSelect={(event) => {
                  setSelectedEventForSale(event);
                  // Load available tickets for this event
                  loadAvailableTicketsForEvent(event._id);
                }}
              />
            </div>

            {selectedEventForSale && (
              <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-2">
                    {t('sales.selectTickets')}
                  </h3>
                  <IonList className="rounded-lg border border-gray-200 dark:border-gray-700">
                    {availableTickets.map((ticket, index) => (
                      <IonItem
                        key={ticket.id || ticket._id || `ticket-${index}`}
                        className="hover:bg-gray-50 dark:hover:bg-gray-800"
                      >
                        <IonLabel>
                          <h4 className="font-medium">{ticket.eventName}</h4>
                          <p className="text-sm text-gray-500">
                            {ticket.venue} - {ticket.section}
                          </p>
                          <p className="text-sm text-gray-500">
                            ${ticket.purchasePrice}
                          </p>
                        </IonLabel>
                        <IonChip
                          color={
                            formData.ticketIds.includes(
                              ticket._id || ticket.id || ''
                            )
                              ? 'primary'
                              : 'medium'
                          }
                          onClick={() => {
                            const ticketId = ticket._id || ticket.id || '';
                            const newIds = formData.ticketIds.includes(ticketId)
                              ? formData.ticketIds.filter(
                                  (id) => id !== ticketId
                                )
                              : [...formData.ticketIds, ticketId];
                            setFormData({ ...formData, ticketIds: newIds });
                          }}
                          className="cursor-pointer"
                        >
                          {formData.ticketIds.includes(
                            ticket._id || ticket.id || ''
                          )
                            ? t('common.selected')
                            : t('common.select')}
                        </IonChip>
                      </IonItem>
                    ))}
                  </IonList>
                </div>

                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-2">
                    {t('sales.buyerInfo')}
                  </h3>
                  <IonList className="space-y-2">
                    <IonItem className="rounded-lg">
                      <IonLabel position="stacked" className="pb-2">
                        {t('sales.buyerName')}
                      </IonLabel>
                      <IonInput
                        value={formData.buyerName}
                        onIonChange={(e) =>
                          setFormData({
                            ...formData,
                            buyerName: e.detail.value!,
                          })
                        }
                        required
                        className="text-lg"
                      />
                    </IonItem>
                    <IonItem className="rounded-lg">
                      <IonLabel position="stacked" className="pb-2">
                        {t('sales.buyerEmail')}
                      </IonLabel>
                      <IonInput
                        type="email"
                        value={formData.buyerEmail}
                        onIonChange={(e) =>
                          setFormData({
                            ...formData,
                            buyerEmail: e.detail.value!,
                          })
                        }
                        required
                        className="text-lg"
                      />
                    </IonItem>
                    <IonItem className="rounded-lg">
                      <IonLabel position="stacked" className="pb-2">
                        {t('sales.buyerPhone')}
                      </IonLabel>
                      <IonInput
                        type="tel"
                        value={formData.buyerPhone}
                        onIonChange={(e) =>
                          setFormData({
                            ...formData,
                            buyerPhone: e.detail.value!,
                          })
                        }
                        required
                        className="text-lg"
                      />
                    </IonItem>
                  </IonList>
                </div>

                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-2">
                    {t('sales.paymentInfo')}
                  </h3>
                  <IonList className="space-y-2">
                    <IonItem className="rounded-lg">
                      <IonLabel position="stacked" className="pb-2">
                        {t('sales.salePrice')}
                      </IonLabel>
                      <IonInput
                        type="number"
                        value={formData.salePrice}
                        onIonChange={(e) =>
                          setFormData({
                            ...formData,
                            salePrice: parseFloat(e.detail.value!),
                          })
                        }
                        required
                        className="text-lg"
                      />
                    </IonItem>
                    <IonItem className="rounded-lg">
                      <IonLabel position="stacked" className="pb-2">
                        {t('sales.paymentMethod')}
                      </IonLabel>
                      <IonSelect
                        value={formData.paymentMethod}
                        onIonChange={(e) =>
                          setFormData({
                            ...formData,
                            paymentMethod: e.detail.value,
                          })
                        }
                      >
                        <IonSelectOption value="cash">
                          {t('sales.cash')}
                        </IonSelectOption>
                        <IonSelectOption value="card">
                          {t('sales.card')}
                        </IonSelectOption>
                        <IonSelectOption value="bankTransfer">
                          {t('sales.bankTransfer')}
                        </IonSelectOption>
                        <IonSelectOption value="other">
                          {t('sales.other')}
                        </IonSelectOption>
                      </IonSelect>
                    </IonItem>
                  </IonList>
                </div>

                {formData.ticketIds.length > 0 && (
                  <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4">
                    <h3 className="font-semibold mb-2">{t('sales.summary')}</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">
                          {t('sales.totalTickets')}:
                        </span>
                        <span className="font-medium">
                          {formData.ticketIds.length}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">
                          {t('sales.totalAmount')}:
                        </span>
                        <span className="font-medium">
                          ${calculateTotalAmount()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">
                          {t('sales.estimatedProfit')}:
                        </span>
                        <span
                          className={`font-medium ${
                            calculateNetProfit() > 0
                              ? 'text-green-600'
                              : 'text-red-600'
                          }`}
                        >
                          ${calculateNetProfit()}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </IonContent>
        </IonModal>

        {/* Action Sheet */}
        <IonActionSheet
          isOpen={showActionSheet}
          onDidDismiss={() => setShowActionSheet(false)}
          buttons={[
            {
              text: t('sales.cancel'),
              icon: banOutline,
              role: 'destructive',
              handler: () => {
                setShowCancelAlert(true);
                return;
              },
            },
            {
              text: t('sales.refund'),
              icon: arrowUndoOutline,
              handler: () => {
                setShowRefundAlert(true);
                return;
              },
            },
            {
              text: t('common.cancel'),
              role: 'cancel',
            },
          ]}
        />

        {/* Cancel Alert */}
        <IonAlert
          isOpen={showCancelAlert}
          onDidDismiss={() => setShowCancelAlert(false)}
          header={t('common.confirm')}
          message={t('sales.cancelConfirm')}
          buttons={[
            {
              text: t('common.cancel'),
              role: 'cancel',
            },
            {
              text: t('sales.cancel'),
              handler: handleCancelSale,
            },
          ]}
        />

        {/* Toast */}
        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={3000}
        />

        {/* Create Event Modal */}
        <IonModal
          isOpen={showCreateEventModal}
          onDidDismiss={() => setShowCreateEventModal(false)}
        >
          <IonHeader>
            <IonToolbar>
              <IonTitle>{t('events.createEvent')}</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowCreateEventModal(false)}>
                  <IonIcon icon={closeCircleOutline} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <CreateEventForm
              onClose={() => setShowCreateEventModal(false)}
              onSuccess={() => {
                setShowCreateEventModal(false);
                // Refresh available tickets after creating event
                if (currentEvent) {
                  loadAvailableTickets();
                }
              }}
            />
          </IonContent>
        </IonModal>

        {/* Import Excel Modal */}
        <IonModal
          isOpen={showImportModal}
          onDidDismiss={() => {
            setShowImportModal(false);
            setImportFile(null);
          }}
        >
          <IonHeader>
            <IonToolbar>
              <IonTitle>Import Sales from Excel</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowImportModal(false)}>
                  Cancel
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <IonCard>
              <IonCardHeader>
                <IonCardTitle>Upload Excel File</IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <div className="space-y-4">
                  <IonText>
                    <p className="text-sm text-gray-600">
                      Please upload an Excel file using the template format.
                      Download the template first to see the required columns.
                    </p>
                  </IonText>

                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setImportFile(file);
                      }
                    }}
                    className="w-full p-2 border rounded"
                  />

                  {importFile && (
                    <IonText color="medium">
                      <p className="text-sm">Selected: {importFile.name}</p>
                    </IonText>
                  )}

                  <div className="flex gap-2 mt-4">
                    <IonButton
                      expand="block"
                      onClick={handleImportExcel}
                      disabled={!importFile || importing}
                      className="flex-1"
                    >
                      {importing ? (
                        <>
                          <IonSpinner name="crescent" className="mr-2" />
                          Importing...
                        </>
                      ) : (
                        'Import Sales'
                      )}
                    </IonButton>

                    <IonButton
                      expand="block"
                      fill="outline"
                      onClick={handleDownloadTemplate}
                      className="flex-1"
                    >
                      <IonIcon icon={downloadOutline} slot="start" />
                      Download Template
                    </IonButton>
                  </div>

                  <IonText color="medium">
                    <p className="text-xs mt-2">
                      Note: Make sure the Event Name in your Excel file matches
                      the selected event.
                    </p>
                  </IonText>
                </div>
              </IonCardContent>
            </IonCard>
          </IonContent>
        </IonModal>

        {/* Source Details Modal */}
        {selectedSaleForSource && (
          <SourceDetailsModal
            isOpen={showSourceModal}
            onClose={() => {
              setShowSourceModal(false);
              setSelectedSaleForSource(null);
            }}
            sourceType={getSourceTypeFromSale(selectedSaleForSource)}
            sourceData={getSourceDataFromSale(selectedSaleForSource)}
            context="sale"
          />
        )}
      </IonContent>
    </IonPage>
  );
};

export default Sales;
